import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api, type ApiNotification, type ApiReadResult } from '@/shared/api';

import { toNotification, type AppNotification } from '../model/types';

/** Laravel's paginator block, which is snake_case unlike the payload. */
type FeedMeta = { current_page?: number; last_page?: number; unread?: number };

export const notificationsKey = (unreadOnly: boolean, page: number) =>
  ['master', 'notifications', unreadOnly, page] as const;

/**
 * The master's feed.
 *
 * Paged rather than showing the first twenty and stopping: a feed that
 * silently ends is indistinguishable from one that has nothing more in it.
 */
export const useNotifications = () => {
  const queryClient = useQueryClient();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);

  const feed = useQuery({
    queryKey: notificationsKey(unreadOnly, page),
    queryFn: () =>
      api.page<ApiNotification[]>(
        `/master/notifications?page=${page}${unreadOnly ? '&unread=1' : ''}`,
      ),
  });

  const meta = (feed.data?.meta ?? {}) as FeedMeta;
  const items: AppNotification[] = (feed.data?.data ?? []).map((dto) => toNotification(dto));

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['master', 'notifications'] });

  const markRead = useMutation({
    mutationFn: (ids: number[]) =>
      api.post<ApiReadResult>('/master/notifications/read', ids.length > 0 ? { ids } : {}),
    onSuccess: invalidate,
  });

  return {
    items,
    unreadCount: meta.unread ?? 0,
    page,
    totalPages: Math.max(1, meta.last_page ?? 1),
    setPage,
    unreadOnly,
    setUnreadOnly: (value: boolean) => {
      setUnreadOnly(value);
      setPage(1);
    },
    isPending: feed.isPending,
    isError: feed.isError,
    markRead: (id: number) => markRead.mutate([id]),
    markAllRead: () => markRead.mutate([]),
  };
};

/** Just the unread count, for the header bell. */
export const useUnreadCount = () => {
  const query = useQuery({
    queryKey: ['master', 'notifications', 'unread-count'],
    queryFn: () => api.page<ApiNotification[]>('/master/notifications?unread=1'),
  });

  return {
    unread: ((query.data?.meta ?? {}) as FeedMeta).unread ?? 0,
    latest: (query.data?.data ?? []).slice(0, 3).map((dto) => toNotification(dto)),
  };
};
